import Close20 from '@carbon/icons-react/es/close/20';
import { Col, FormInstance, Row as AntRow, Tooltip } from 'antd';
import { FormListFieldData, FormListOperation } from 'antd/es/form/FormList';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  SortableContainer,
  SortableElement,
  SortEndHandler,
} from 'react-sortable-hoc';
import styled, { css } from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldMetaDataOption } from 'api/fields';
import { Button } from 'components/atoms/button';
import { DragHandle } from 'components/atoms/drag-handle';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Color } from 'components/colors';
import { useTranslation } from 'hooks/use-translation';
import { FieldOption, Values } from '../types';
import { ColorDropdown, getArbitraryColor } from './color-dropdown';

const Row = styled(AntRow)`
  z-index: var(--zindex-popover-low);
`;

const FormItem = styled(Form.Item)`
  && {
    margin-bottom: var(--layout-01);
  }
`;

const Circle = styled.span<{ color: Color }>`
  --tag-border-color: var(--tag-surface-color);
  height: 24px;
  width: 24px;
  border-radius: 50%;
  ${({ color }) => css`
    --tag-surface-color: var(--ozone-${color}-surface);
    --tag-color: var(--ozone-${color});
  `};
  border: 1px solid var(--tag-border-color);
  background: var(--tag-surface-color);
  color: var(--tag-color);

  display: flex;
  align-items: center;
  justify-content: center;
`;

export const addOption = (
  add: FormListOperation['add'],
): ((index: number) => void) => {
  return (index) =>
    add({
      deletable: true,
      editable: true,
      label: '',
      color: getArbitraryColor(index),
    } as FieldOption);
};

export type ListOperation = Pick<FormListOperation, 'remove'> & {
  add: (index: number) => void;
  objectType: FieldSectionType;
};

export interface ItemProps extends ListOperation {
  formField: FormListFieldData;
  form: FormInstance<Values>;

  options: FieldMetaDataOption[];
}

const generateChar = (seed = '') => {
  return seed.length > 0 ? seed[0] : 'A';
};

const SortableItem = SortableElement<ItemProps>(
  ({ formField, form, add, remove, options }: ItemProps) => {
    const requiredMessage = useTranslation('input.required');
    const sameStatusMessage = useTranslation('form.validations.same_statuses');
    const currentOptionFieldPath = ['metaData', 'options', formField.name];
    const currentOption = form.getFieldValue(currentOptionFieldPath) as
      | FieldOption
      | undefined;
    const isEditable = currentOption?.editable;
    const isDeletable = currentOption?.deletable;

    return currentOption ? (
      <Row gutter={8}>
        <Col flex="40px">
          <DragHandle size="small" />
        </Col>
        <Col flex="40px">
          <FormItem
            {...formField}
            name={[formField.name, 'color']}
            fieldKey={[formField.fieldKey, 'color']}
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
            initialValue={getArbitraryColor(formField.name)}
          >
            <ColorDropdown
              size="small"
              IconComponent={({ color }) => (
                <Circle color={color}>
                  {generateChar(currentOption?.label)}
                </Circle>
              )}
              option={currentOption}
            />
          </FormItem>
        </Col>
        <Col flex="352px">
          <Form.Item
            {...formField}
            name={[formField.name, 'label']}
            fieldKey={[formField.fieldKey, 'label']}
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
              {
                message: sameStatusMessage,
                validator: (_, value) => {
                  const isOptionAvailable = options?.filter(
                    (option) =>
                      option.label.trim().toLowerCase() ===
                      value.trim().toLowerCase(),
                  );

                  return isOptionAvailable.length > 1
                    ? Promise.reject()
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              size="small"
              disabled={!isEditable}
              autoFocus
              onPressEnter={() => add(formField.name + 1)}
            />
          </Form.Item>
        </Col>

        {isDeletable && (
          <Col flex="40px">
            <Form.Item shouldUpdate>
              <Tooltip
                mouseEnterDelay={1}
                title={<FormattedMessage id="settings.fields.remove_option" />}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<Close20 />}
                  onClick={() => remove(formField.name)}
                />
              </Tooltip>
            </Form.Item>
          </Col>
        )}
      </Row>
    ) : null;
  },
);

export interface SortableOptionsProps extends ListOperation {
  fields: FormListFieldData[];
  form: FormInstance<Values>;
  onSortEnd: SortEndHandler;
  options: FieldMetaDataOption[];
}

export const SortableOptions = SortableContainer<SortableOptionsProps>(
  ({
    fields,
    form,
    add,
    remove,
    objectType,
    options,
  }: SortableOptionsProps) => {
    return (
      <div>
        {fields.map((formField) => (
          <SortableItem
            key={`formField-${formField.name}`}
            index={formField.name}
            formField={formField}
            form={form}
            add={add}
            remove={remove}
            objectType={objectType}
            options={options}
          />
        ))}
      </div>
    );
  },
);
